/**
 * Referral Acceptance Component
 *
 * Displays accept/reject buttons for referral and handles the acceptance workflow.
 * Shows acceptance status, PHI email confirmation, and audit log.
 *
 * Features:
 * - Accept/Reject buttons with loading states
 * - Status badges (pending, accepted, rejected, expired)
 * - PHI email sent confirmation
 * - Expiration countdown
 * - Rejection reason input
 * - Error handling
 */

import { useState, useEffect } from 'react';
import { Check, X, Clock, AlertCircle, Mail, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { config } from '@/src/config';
import { useFormattedDate } from '../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';

interface ReferralAcceptanceProps {
  referralId: string;
  providerId: string;
  onAccept?: () => void;
  onReject?: () => void;
}

type AcceptanceStatus = 'pending' | 'accepted' | 'rejected' | 'expired';

export function ReferralAcceptance({
  referralId,
  providerId,
  onAccept,
  onReject
}: ReferralAcceptanceProps) {
  const { fmt } = useFormattedDate();
  const [status, setStatus] = useState<AcceptanceStatus>('pending');
  const [loading, setLoading] = useState(false);
  const [acceptedAt, setAcceptedAt] = useState<string | null>(null);
  const [rejectedAt, setRejectedAt] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [phiEmailSent, setPhiEmailSent] = useState(false);
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmailPreview, setShowEmailPreview] = useState(false);

  // Fetch current status on mount
  useEffect(() => {
    fetchStatus();
  }, [referralId, providerId]);

  const fetchStatus = async () => {
    try {
      const response = await authFetch(`${config.api.baseUrl}/api/referrals/${referralId}/status?provider_id=${providerId}`);

      if (response.ok) {
        const data = await response.json();
        setStatus(data.acceptance_status || 'pending');
        setAcceptedAt(data.accepted_at);
        setRejectedAt(data.rejected_at);
        setExpiresAt(data.expires_at);
        setPhiEmailSent(data.phi_shared || false);
        setEmailSentTo(data.email_sent_to || null);
      }
    } catch (err) {
      console.error('Error fetching referral status:', err);
    }
  };

  const handleAccept = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(`${config.api.baseUrl}/api/referrals/${referralId}/accept`, {
        method: 'POST',
        jsonBody: { provider_id: providerId }
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('accepted');
        setAcceptedAt(data.accepted_at);
        setPhiEmailSent(data.phi_email_sent);
        setEmailSentTo(data.email_sent_to);

        if (onAccept) onAccept();
      } else {
        setError(data.error || data.reason || 'Failed to accept referral');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error accepting referral:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(`${config.api.baseUrl}/api/referrals/${referralId}/reject`, {
        method: 'POST',
        jsonBody: {
            provider_id: providerId,
            reason: rejectionReason
          }
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('rejected');
        setRejectedAt(data.rejected_at);
        setShowRejectDialog(false);

        if (onReject) onReject();
      } else {
        setError(data.error || data.reason || 'Failed to reject referral');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error rejecting referral:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: AcceptanceStatus) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'expired':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    }
  };

  const getDaysUntilExpiration = () => {
    if (!expiresAt) return null;
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const daysLeft = getDaysUntilExpiration();

  // Accepted State
  if (status === 'accepted') {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-green-900 text-lg">Referral Accepted</h3>
              <p className="text-sm text-green-700 mt-1">
                You accepted this referral on {acceptedAt && fmt.dateTime(acceptedAt)}
              </p>
            </div>
            <Badge className={getStatusColor(status)}>
              ACCEPTED
            </Badge>
          </div>
        </div>

        {phiEmailSent && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-blue-900">Full Member Details Sent</p>
                <p className="text-sm text-blue-700 mt-1">
                  Complete member information with PHI has been sent to{' '}
                  <span className="font-medium">{emailSentTo}</span>
                </p>
                <p className="text-xs text-blue-600 mt-2">
                  📧 Check your email for the confidential referral details
                </p>
              </div>
            </div>
          </div>
        )}

        {!phiEmailSent && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-semibold text-yellow-900">Email Pending</p>
                <p className="text-sm text-yellow-700 mt-1">
                  Full member details will be sent to your email shortly
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Rejected State
  if (status === 'rejected') {
    return (
      <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <X className="w-6 h-6 text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-red-900 text-lg">Referral Rejected</h3>
            <p className="text-sm text-red-700 mt-1">
              You rejected this referral on {rejectedAt && fmt.dateTime(rejectedAt)}
            </p>
          </div>
          <Badge className={getStatusColor(status)}>
            REJECTED
          </Badge>
        </div>
      </div>
    );
  }

  // Expired State
  if (status === 'expired') {
    return (
      <div className="p-4 bg-gray-50 border-2 border-gray-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-gray-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 text-lg">Referral Expired</h3>
            <p className="text-sm text-gray-700 mt-1">
              This referral expired on {expiresAt && fmt.dateTime(expiresAt)}
            </p>
          </div>
          <Badge className={getStatusColor(status)}>
            EXPIRED
          </Badge>
        </div>
      </div>
    );
  }

  // Pending State - Show Accept/Reject Buttons
  return (
    <div className="space-y-4">
      {/* Expiration Warning */}
      {daysLeft !== null && daysLeft <= 3 && (
        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-orange-600" />
            <p className="text-sm text-orange-800">
              <span className="font-semibold">Expiring Soon:</span> This referral expires in{' '}
              <span className="font-bold">{daysLeft} day{daysLeft !== 1 ? 's' : ''}</span>
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Status Badge */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">Referral Status</h3>
          <p className="text-sm text-gray-600 mt-1">Please review and respond to this referral</p>
        </div>
        <Badge className={getStatusColor(status)}>
          {status.toUpperCase()}
        </Badge>
      </div>

      {/* PHI Notice */}
      <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-purple-600 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-purple-900">Secure PHI Delivery</p>
            <p className="text-sm text-purple-700 mt-1">
              Upon acceptance, complete member details including PHI will be securely sent to your registered email address
            </p>
            <button
              onClick={() => setShowEmailPreview(!showEmailPreview)}
              className="text-sm text-purple-600 hover:text-purple-800 underline mt-2"
            >
              {showEmailPreview ? 'Hide' : 'Preview'} email content
            </button>
          </div>
        </div>
      </div>

      {/* Email Preview (Collapsible) */}
      {showEmailPreview && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-start gap-2 mb-3">
            <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-gray-900">Email Preview</p>
              <p className="text-xs text-gray-600 mt-1">
                Subject: <span className="font-mono">CONFIDENTIAL - Full Referral Details: {referralId}</span>
              </p>
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded p-4 text-sm space-y-3">
            <div className="bg-blue-600 text-white p-3 rounded text-center">
              <p className="font-bold">🔒 CONFIDENTIAL REFERRAL DETAILS</p>
              <p className="text-xs mt-1">Thank you for accepting this referral</p>
            </div>

            <div className="space-y-2">
              <p className="font-semibold text-gray-700 border-b pb-1">📋 What's Included:</p>
              <ul className="text-xs text-gray-600 space-y-1 ml-4">
                <li>• <strong>Personal Details:</strong> Full name, date of birth, age, preferred language</li>
                <li>• <strong>Contact Information:</strong> Phone, email, complete address</li>
                <li>• <strong>Insurance:</strong> Medicaid ID, health plan, plan type</li>
                <li>• <strong>Medical Info:</strong> Primary diagnosis, risk score</li>
                <li>• <strong>Referral Details:</strong> Urgency level, SDOH needs, authorization requirements</li>
                <li>• <strong>Next Steps:</strong> Timeline for member contact and care coordination</li>
              </ul>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
              <p className="text-xs font-semibold text-yellow-800">⏰ NEXT STEPS:</p>
              <ul className="text-xs text-yellow-700 mt-1 ml-3 space-y-0.5">
                <li>• Contact member within 48 hours</li>
                <li>• Verify insurance eligibility</li>
                <li>• Complete prior authorization if required</li>
              </ul>
            </div>

            <div className="bg-red-50 border border-red-200 p-3 rounded">
              <p className="text-xs font-semibold text-red-800">⚠️ HIPAA CONFIDENTIALITY NOTICE</p>
              <p className="text-xs text-red-700 mt-1">
                This email contains Protected Health Information (PHI). Unauthorized disclosure is strictly prohibited.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {!showRejectDialog ? (
        <div className="flex gap-3">
          <Button
            onClick={handleAccept}
            disabled={loading}
            className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Processing...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                Accept Referral
              </span>
            )}
          </Button>
          <Button
            onClick={() => setShowRejectDialog(true)}
            disabled={loading}
            className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            <span className="flex items-center gap-2">
              <X className="w-5 h-5" />
              Reject Referral
            </span>
          </Button>
        </div>
      ) : (
        // Rejection Dialog
        <div className="p-4 bg-white border-2 border-red-200 rounded-lg space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Rejection *
            </label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="e.g., Not accepting new patients, Services not available, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              rows={3}
              disabled={loading}
            />
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleReject}
              disabled={loading || !rejectionReason.trim()}
              className="flex-1 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Submitting...' : 'Confirm Rejection'}
            </Button>
            <Button
              onClick={() => {
                setShowRejectDialog(false);
                setRejectionReason('');
                setError(null);
              }}
              disabled={loading}
              className="flex-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:bg-gray-100"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReferralAcceptance;
